import { Component, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { FieldType, FieldTypeConfig, FormlyModule } from "@ngx-formly/core";
import { TranslateModule } from "@ngx-translate/core";

export interface PercentageField {
  key: string;
  label: string;
}

/**
 * Custom Formly field type for percentage input groups.
 * Handles multiple percentage fields that must sum to 100.
 * When a field is changed, the others are auto-corrected if the total exceeds 100.
 *
 * Usage:
 * ```typescript
 * {
 *   key: 'matchcriterion',
 *   type: 'percentage-group',
 *   props: {
 *     label: 'Weighting percentages',
 *     fields: [
 *       { key: 'companyCultureFactor', label: 'Company culture' },
 *       { key: 'personalityFactor', label: 'Personality' },
 *       { key: 'areaOfInterestFactor', label: 'Field of interest' }
 *     ]
 *   }
 * }
 * ```
 */
@Component({
  selector: "app-formly-percentage-group",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, FormlyModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (props.label) {
      <h3>{{ props.label | translate }}</h3>
    }
    <div class="percentage-group">
      @for (percentageField of percentageFields; track percentageField.key) {
        <div class="form-group row">
          <label class="col-form-label col-12" [attr.for]="percentageField.key">
            {{ percentageField.label | translate }}
          </label>
          <div class="col-12 col-sm-6">
            <div class="input-group">
              <input
                class="form-control"
                type="number"
                min="0"
                max="100"
                [id]="percentageField.key"
                [value]="getFieldValue(percentageField.key)"
                (change)="onFactorChange(percentageField.key, $event)"
                (keyup)="onFactorChange(percentageField.key, $event)"
              />
              <div class="input-group-append">
                <span class="input-group-text">%</span>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .percentage-group {
        margin-bottom: 1rem;
      }
    `,
  ],
})
export class PercentageGroupFieldComponent extends FieldType<FieldTypeConfig> {
  override defaultOptions = {
    props: {
      fields: [] as PercentageField[],
    },
  };

  get percentageFields(): PercentageField[] {
    return Array.isArray(this.props["fields"]) ? this.props["fields"] : [];
  }

  getFieldValue(key: string): number {
    const model = this.model as Record<string, number>;
    return model?.[key] ?? 0;
  }

  onFactorChange(changedKey: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const newValue = parseInt(input.value, 10) || 0;
    const fields = this.percentageFields;

    // Get current values
    const values: Record<string, number> = {};
    let total = 0;

    for (const field of fields) {
      if (field.key === changedKey) {
        values[field.key] = newValue;
      } else {
        values[field.key] = this.getFieldValue(field.key);
      }
      total += values[field.key];
    }

    // If total exceeds 100, adjust the changed field
    if (total > 100) {
      const otherFieldsTotal = total - newValue;
      values[changedKey] = Math.max(0, 100 - otherFieldsTotal);
    }

    // Update the model
    const model = { ...this.model } as Record<string, number>;
    for (const key of Object.keys(values)) {
      model[key] = values[key];
    }

    this.formControl.setValue(model);
  }
}
